import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, Mail, Sparkles, ChevronDown } from "lucide-react";
import { Button } from "../ui/button";
import { navLinks, exploreLinks, schoolInfo } from "../../mock/mock";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setIsOpen(false), [location.pathname]);

  return (
    <header className="sticky top-0 z-50">
      {/* Top strip */}
      <div className="bg-gradient-to-r from-orange-700 to-orange-600 text-white text-xs py-2 px-4 hidden md:block">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center gap-5">
            <a href={`tel:${schoolInfo.phone}`} className="flex items-center gap-1.5 hover:text-yellow-200 transition-colors">
              <Phone className="w-3.5 h-3.5" /> {schoolInfo.phone}
            </a>
            <a href={`mailto:${schoolInfo.email}`} className="flex items-center gap-1.5 hover:text-yellow-200 transition-colors">
              <Mail className="w-3.5 h-3.5" /> {schoolInfo.email}
            </a>
          </div>
          <div className="flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5 text-yellow-200" />
            <span className="font-medium">Grand Launch — 1st January 2027</span>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <nav className={`transition-all duration-300 ${scrolled ? "bg-white/95 backdrop-blur-md shadow-lg" : "bg-white"}`}>
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <img
              src={schoolInfo.logoUrl}
              alt="Aadi Gayanam Logo"
              className="w-14 h-14 rounded-full object-cover shadow-md group-hover:scale-105 transition-transform border-2 border-orange-100 bg-white"
            />
            <div>
              <h1 className="font-display font-bold text-xl md:text-2xl leading-none text-orange-800">
                Aadi Gayanam
              </h1>
              <p className="text-[10px] md:text-xs text-amber-700 tracking-widest uppercase font-semibold">
                Play School • Jaipur
              </p>
            </div>
          </Link>

          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-all relative ${
                  location.pathname === link.path
                    ? "text-orange-700 bg-orange-50"
                    : "text-stone-700 hover:text-orange-700 hover:bg-orange-50"
                }`}
              >
                {link.label}
                {location.pathname === link.path && (
                  <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1.5 h-1.5 bg-orange-600 rounded-full" />
                )}
              </Link>
            ))}
            {/* Explore Dropdown */}
            <div className="relative group">
              <button
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-all flex items-center gap-1 ${
                  exploreLinks.some((l) => l.path === location.pathname)
                    ? "text-orange-700 bg-orange-50"
                    : "text-stone-700 hover:text-orange-700 hover:bg-orange-50"
                }`}
              >
                Explore <ChevronDown className="w-3.5 h-3.5" />
              </button>
              <div className="absolute top-full right-0 mt-2 w-72 bg-white rounded-2xl shadow-2xl border border-orange-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 overflow-hidden">
                <div className="p-2">
                  {exploreLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      className="flex items-start gap-3 p-3 rounded-xl hover:bg-orange-50 transition-colors"
                    >
                      <div className="w-2 h-2 rounded-full bg-orange-500 mt-2 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-sm text-stone-800">{link.label}</p>
                        <p className="text-xs text-stone-500">{link.desc}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="hidden lg:block">
            <Link to="/admission">
              <Button className="bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 text-white rounded-full px-6 shadow-md btn-shine">
                Enroll Now
              </Button>
            </Link>
          </div>

          <button className="lg:hidden p-2 text-orange-700" onClick={() => setIsOpen(!isOpen)}>
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile menu */}
        {isOpen && (
          <div className="lg:hidden bg-white border-t border-orange-100 px-4 py-3 shadow-lg">
            <div className="flex flex-col gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-4 py-3 rounded-lg text-sm font-semibold ${
                    location.pathname === link.path
                      ? "text-orange-700 bg-orange-50"
                      : "text-stone-700 hover:bg-orange-50"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <div className="border-t border-orange-100 mt-2 pt-2">
                <p className="px-4 py-1 text-[10px] font-bold text-orange-700 tracking-widest uppercase">Explore</p>
                {exploreLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`px-4 py-3 rounded-lg text-sm font-semibold block ${
                      location.pathname === link.path
                        ? "text-orange-700 bg-orange-50"
                        : "text-stone-700 hover:bg-orange-50"
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
              <Link to="/admission">
                <Button className="w-full mt-2 bg-gradient-to-r from-orange-600 to-amber-600 text-white rounded-full">
                  Enroll Now
                </Button>
              </Link>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Navbar;
